'use client';

import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Button } from '@mui/material';
import useThemeMediaQuery from '@fuse/hooks/useThemeMediaQuery';
import FusePageSimple from '@fuse/core/FusePageSimple';
import usePathname from '@fuse/hooks/usePathname';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import BillingAppHeader from './BillingAppHeader';
import AddNewCardModal from './AddNewCardModal';

const Root = styled(FusePageSimple)(() => ({
	'& .FusePageCarded-header': {},
	'& .FusePageCarded-sidebar': {},
	'& .FusePageCarded-leftSidebar': {}
}));

type BillingAppProps = {
	children: React.ReactNode;
};

/**
 * The notes app.
 */
const PAYMENT_METHOD_PATH = '/apps/billing/payment-method';

function BillingApp(props: BillingAppProps) {
	const { children } = props;

	const pathname = usePathname();
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
	const [leftSidebarOpen, setLeftSidebarOpen] = useState(!isMobile);
	const [paymentMethodAddCardOpen, setPaymentMethodAddCardOpen] = useState(false);
	const isPaymentMethodPage = pathname === PAYMENT_METHOD_PATH;

	useEffect(() => {
		setLeftSidebarOpen(!isMobile);
	}, [isMobile]);

	useEffect(() => {
		if (isMobile) {
			setLeftSidebarOpen(false);
		}
	}, [pathname, isMobile]);

	useEffect(() => {
		if (!isPaymentMethodPage) {
			setPaymentMethodAddCardOpen(false);
		}
	}, [isPaymentMethodPage]);

	return (
		<Root
			content={
				<div className="flex-auto px-4">
					<BillingAppHeader
						className="py-6 sm:py-8"
						onSetSidebarOpen={setLeftSidebarOpen}
						headerAction={
							isPaymentMethodPage ? (
								<Button
									variant="contained"
									color="secondary"
									className="shadow-md"
									onClick={() => setPaymentMethodAddCardOpen(true)}
								>
									<FuseSvgIcon size={20}>heroicons-outline:plus</FuseSvgIcon>
									<span className="mx-1 sm:mx-2">Add New Card</span>
								</Button>
							) : undefined
						}
					/>
					{children}
					{isPaymentMethodPage && (
						<AddNewCardModal
							open={paymentMethodAddCardOpen}
							onClose={() => setPaymentMethodAddCardOpen(false)}
						/>
					)}
				</div>
			}
		/>
	);
}

export default BillingApp;
